// Migração de painel: trazer um cliente de um WHM/cPanel.
//
// A TELA ENFILEIRA, ELA NÃO EXECUTA. Quem tem a chave SSH é um processo
// separado, que roda fora da API e não escuta em porta nenhuma — a API é o
// processo exposto à internet, e uma credencial root para o servidor do
// cliente não pode morar nela.
//
// Por isso o formulário pede host, porta e usuário, e NÃO pede a chave: ela
// se instala uma vez por servidor, com `ssh-copy-id`. A tela diz isso, porque
// um campo que falta sem explicação parece defeito.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { caixas, migracao, revendas } from "../api/admin";
import { ErroDaAPI, explicar } from "../api/problema";
import type { ServidorOrigem, TrabalhoMigracao } from "../api/tipos";

/** Os painéis de origem, VINDO DO CONTRATO.
 *
 * Escrito à mão era `"whm" | "imap"`, em quatro lugares. Quando o
 * `directadmin` entrou no contrato, nada quebrou na compilação — a tela
 * simplesmente não o oferecia, e o backend ficou pronto sem ninguém
 * conseguir usar. Derivar do contrato faz a próxima adição quebrar aqui. */
type TipoDeOrigem = NonNullable<ServidorOrigem["tipo"]>;

// Espelha Progresso, em interno/migracao/progresso.go.
type ProgressoMigracao = {
  fase: string;
  caixa?: string;
  caixas_feitas: number;
  caixas_total: number;
  pasta?: string;
  pasta_feitas: number;
  pasta_total: number;
  caixa_feitas: number;
  caixa_total: number;
  importadas: number;
  puladas: number;
  bytes: number;
};

// O que o estágio `dominios` devolve por linha. Espelha DominioNaOrigem, em
// interno/migracao/inventario.go.
type DominioDaOrigem = {
  dominio: string;
  conta: string;
  caixas: number;
  existe_aqui: boolean;
  ja_migrado: boolean;
  pouso_bytes?: number;
  caixas_aqui: number;
  mensagens_aqui: number;
  ultima_sincronizacao?: string;
};
import { useSessao } from "../sessao/contexto";
import { Icone } from "../ui/Icone";
import { dataCurta } from "../ui/formato";
import { Botao, Cabecalho, Celula, Estado, Linha, Selo, Tabela } from "./comuns";

export function Migracao() {
  const fila = useQueryClient();
  // A REVENDA VÊ SÓ O IMAP (pedido do Jhou, 31/10/2026). Nada do WHM/cPanel
  // e do DirectAdmin é desenhado para ela: nem as abas, nem os servidores
  // cadastrados, nem a explicação da chave SSH. Quem recusa de verdade é a
  // API (migracoes_revenda.go); a tela só não oferece o que não vai valer.
  const { podeVer } = useSessao();
  const soIMAP = !podeVer("super_admin");
  const [cadastrando, setCadastrando] = useState(false);
  const [erro, setErro] = useState("");
  // DOIS MIGRADORES, DUAS ABAS. Eles não competem: o de WHM traz o cliente
  // inteiro sem ninguém trocar de senha, e só existe quando há SSH; o de IMAP
  // traz uma caixa de qualquer servidor, ao custo de o cliente trocar. Pôr os
  // dois na mesma tela faria metade dos campos não se aplicar sempre.
  const [aba, setAba] = useState<"whm" | "caixa">(soIMAP ? "caixa" : "whm");

  const servidores = useQuery({
    queryKey: ["p", "migracao", "servidores"],
    queryFn: () => migracao.servidores(),
    // Os servidores cadastrados são da plataforma: para a revenda a API
    // responde 403, e perguntar só produziria um erro na tela.
    enabled: !soIMAP,
  });
  // O histórico mostra MIGRAÇÕES. O estágio `dominios` é a consulta que
  // alimenta o seletor de domínio — cada abertura da tela cria uma, e
  // listá-las junto afogaria as migrações de verdade no meio de consultas.
  const ehMigracao = (t: TrabalhoMigracao) => t.estagio !== "dominios";

  const trabalhos = useQuery({
    queryKey: ["p", "migracao", "trabalhos"],
    queryFn: () => migracao.trabalhos(),
    // O trabalhador roda fora da API; a tela só descobre o resultado
    // perguntando. Mas perguntar de 2 em 2 segundos a noite toda num painel
    // que alguém deixou aberto é desperdício: o ritmo acelera enquanto há
    // trabalho correndo e afrouxa quando a fila está parada.
    refetchInterval: (q) => {
      const correndo = q.state.data?.itens?.some(
        (t) => t.estado === "rodando" || t.estado === "na fila",
      );
      return correndo ? 2_000 : 15_000;
    },
  });

  const recarregar = () => void fila.invalidateQueries({ queryKey: ["p", "migracao"] });
  const mensagem = (e: unknown) =>
    e instanceof ErroDaAPI ? (Object.values(e.porCampo)[0]?.[0] ?? e.mensagem) : explicar(e);

  return (
    <>
      <Cabecalho
        titulo="Migração"
        descricao={
          aba === "whm"
            ? "Trazer um cliente inteiro de um WHM/cPanel, com as senhas que ele já usa."
            : "Trazer as mensagens de uma caixa, de qualquer servidor IMAP."
        }
        acao={
          aba === "whm" && !soIMAP ? (
            <Botao icone="Plus" aoClicar={() => { setErro(""); setCadastrando((c) => !c); }}>
              Novo servidor
            </Botao>
          ) : undefined
        }
      />

      {/* A ESCOLHA VEM ANTES DE TUDO porque ela muda o que se pede e o que a
          migração consegue trazer. O rótulo diz a CONSEQUÊNCIA, não o
          protocolo: "sem trocar senha" é o que decide, e "SSH" não diz nada a
          quem está escolhendo. */}
      {!soIMAP && (
        <div className="mb-5 flex flex-wrap gap-2">
          {([
            ["whm", "Cliente inteiro (WHM/cPanel)", "Traz tudo, sem trocar as senhas"],
            ["caixa", "Uma caixa (IMAP)", "Qualquer servidor, só as mensagens"],
          ] as const).map(([valor, titulo, detalhe]) => (
            <button
              key={valor}
              type="button"
              onClick={() => { setErro(""); setAba(valor); }}
              className={`flex-1 rounded-xl border px-4 py-3 text-left transition ${
                aba === valor
                  ? "border-acao bg-acao/10"
                  : "border-linha bg-white hover:border-acao/40"
              }`}
            >
              <span className="block text-sm font-medium text-tinta">{titulo}</span>
              <span className="block text-xs text-tinta-fraca">{detalhe}</span>
            </button>
          ))}
        </div>
      )}

      {aba === "caixa" && (
        <MigrarUmaCaixa soIMAP={soIMAP} aoMudar={recarregar} aoErrar={(e) => setErro(mensagem(e))} />
      )}

      {!soIMAP && (
        <>
          {/* A explicação da chave vem ANTES do formulário, não depois: quem
              chega aqui procurando onde põe a senha do SSH precisa entender por
              que ela não está sendo pedida, antes de concluir que faltou campo. */}
          <div className="mb-5 rounded-xl border border-linha bg-fundo p-4 text-sm text-tinta-media">
            <p className="flex items-center gap-2 font-medium text-tinta">
              <Icone nome="KeyRound" tamanho={16} className="text-acao" />
              A chave SSH não é pedida aqui, e isso é de propósito
            </p>
            <p className="mt-1.5">
              O painel guarda só as coordenadas do servidor. A credencial fica na
              máquina, e quem a usa é um processo separado — esta aplicação está
              exposta à internet, e uma chave root para o servidor do seu cliente
              não pode morar nela.
            </p>
            <p className="mt-2">
              Instale a chave uma vez por servidor de origem, e deixe o trabalhador
              rodando:
            </p>
            <pre className="mt-2 overflow-x-auto rounded-lg bg-white px-3 py-2 text-xs text-tinta">
{`ssh-copy-id root@servidor-de-origem
make migrar-trabalhador`}
            </pre>
          </div>

          {cadastrando && (
            <FormaDeServidor
              aoFechar={() => setCadastrando(false)}
              aoSalvar={() => { setCadastrando(false); recarregar(); }}
              aoErrar={(e) => setErro(mensagem(e))}
            />
          )}
        </>
      )}
      {erro && <p className="mb-4 text-sm text-perigo">{erro}</p>}

      {!soIMAP && (
        <>
          <h2 className="mb-2 text-sm font-medium text-tinta">Servidores de origem</h2>
          <Estado consulta={servidores} vazio={servidores.data?.itens?.length === 0}>
            <Tabela colunas={["Nome", "Endereço", "Trabalhos", ""]}>
              {servidores.data?.itens?.map((s) => (
                <LinhaDoServidor key={s.uuid} servidor={s} aoMudar={recarregar} aoErrar={(e) => setErro(mensagem(e))} />
              ))}
            </Tabela>
          </Estado>
        </>
      )}

      <h2 className="mt-8 mb-2 text-sm font-medium text-tinta">{soIMAP ? "Migrações" : "Conferências"}</h2>
      <p className="mb-2 text-xs text-tinta-fraca">
        {soIMAP
          ? "Testar só lê. Migrar copia as mensagens da origem para a caixa daqui, " +
            "sem apagar nada lá; rodar de novo traz só o que ainda não veio."
          : "Conferir só LÊ — não altera nada na origem nem aqui. Rode quantas vezes " +
            "quiser, inclusive com o servidor do cliente em produção."}
      </p>
      <Estado consulta={trabalhos} vazio={(trabalhos.data?.itens ?? []).filter(ehMigracao).length === 0}>
        <Tabela colunas={soIMAP
          ? ["Quando", "Origem", "Domínio", "Estado", ""]
          : ["Quando", "Servidor", "Conta", "Domínio", "Estado", ""]}>
          {(trabalhos.data?.itens ?? []).filter(ehMigracao).map((t) => (
            <LinhaDoTrabalho key={t.uuid} trabalho={t} aoMudar={recarregar} semConta={soIMAP} />
          ))}
        </Tabela>
      </Estado>
    </>
  );
}

function FormaDeServidor({
  aoFechar,
  aoSalvar,
  aoErrar,
}: {
  aoFechar: () => void;
  aoSalvar: () => void;
  aoErrar: (e: unknown) => void;
}) {
  const [campos, setCampos] = useState({
    nome: "",
    host: "",
    porta: "22",
    usuario: "root",
    observacoes: "",
    tipo: "whm" as TipoDeOrigem,
  });
  const ehIMAP = campos.tipo === "imap";
  // A PORTA ACOMPANHA O TIPO. 22 é SSH, 993 é IMAP sobre TLS. Deixar 22 ao
  // trocar para IMAP produziria uma falha de conexão que não ensina nada.
  const trocarTipo = (tipo: TipoDeOrigem) =>
    setCampos((c) => ({
      ...c,
      tipo,
      porta: tipo === "imap" ? "993" : "22",
      usuario: tipo === "imap" ? "" : "root",
    }));

  const salvar = useMutation({
    mutationFn: () =>
      migracao.cadastrarServidor({
        nome: campos.nome.trim(),
        host: campos.host.trim(),
        porta: Number.parseInt(campos.porta, 10) || (ehIMAP ? 993 : 22),
        // No IMAP não existe "usuário do servidor": quem identifica é o
        // endereço de cada caixa, pedido na hora de migrar.
        usuario: ehIMAP ? "imap" : campos.usuario.trim() || "root",
        observacoes: campos.observacoes.trim() || undefined,
        tipo: campos.tipo,
      }),
    onSuccess: aoSalvar,
    onError: aoErrar,
  });

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); salvar.mutate(); }}
      className="mb-5 grid gap-3 rounded-xl border border-linha bg-fundo p-4 sm:grid-cols-4"
    >
      {/* O TIPO VEM PRIMEIRO porque ele muda todo o resto — a porta, o que
          se pede, e o que a migração consegue trazer. Perguntar depois faria
          a pessoa preencher campos que não se aplicam. */}
      <label className="text-xs text-tinta-fraca sm:col-span-4">
        Tipo de origem
        <div className="mt-1 flex flex-wrap gap-2">
          {([
            ["whm", "WHM / cPanel (por SSH)", "Traz tudo: senhas, cotas, encaminhadores, agenda."],
            ["directadmin", "DirectAdmin (por SSH)", "Traz tudo: senhas, cotas e encaminhadores."],
            ["imap", "Outro servidor (por IMAP)", "Traz só as mensagens, uma caixa por vez."],
          ] as const).map(([valor, titulo, detalhe]) => (
            <button
              key={valor}
              type="button"
              onClick={() => trocarTipo(valor)}
              className={`flex-1 rounded-lg border px-3 py-2 text-left text-xs ${
                campos.tipo === valor
                  ? "border-acao bg-acao/10 text-tinta"
                  : "border-linha bg-white text-tinta-media"
              }`}
            >
              <span className="block font-medium">{titulo}</span>
              <span className="block text-tinta-fraca">{detalhe}</span>
            </button>
          ))}
        </div>
      </label>

      <Campo rotulo="Nome" valor={campos.nome} aoMudar={(v) => setCampos((c) => ({ ...c, nome: v }))}
        dica="Como você chama esse servidor." classe="sm:col-span-2" />
      <Campo rotulo="Host ou IP" valor={campos.host} aoMudar={(v) => setCampos((c) => ({ ...c, host: v }))}
        dica={ehIMAP
          ? "O servidor IMAP de origem, ex.: mail.cliente.com"
          : "O endereço do painel de origem."}
        classe="sm:col-span-2" />
      <Campo rotulo={ehIMAP ? "Porta IMAP" : "Porta SSH"} valor={campos.porta}
        aoMudar={(v) => setCampos((c) => ({ ...c, porta: v }))} tipo="number"
        dica={ehIMAP ? "993 = TLS direto." : undefined} />
      {!ehIMAP && (
        <Campo rotulo="Usuário" valor={campos.usuario} aoMudar={(v) => setCampos((c) => ({ ...c, usuario: v }))}
          dica={campos.tipo === "directadmin"
            ? "Precisa ler /etc/virtual e /home."
            : "Precisa ler /home e /etc/valiases."} />
      )}
      <Campo rotulo="Observações" valor={campos.observacoes}
        aoMudar={(v) => setCampos((c) => ({ ...c, observacoes: v }))} classe="sm:col-span-2" />
      <div className="flex items-center gap-2 sm:col-span-4">
        <Botao tipo="submit" icone="Plus" ocupado={salvar.isPending}>Salvar</Botao>
        <Botao tom="neutro" aoClicar={aoFechar}>Cancelar</Botao>
      </div>
    </form>
  );
}

function LinhaDoServidor({
  servidor,
  aoMudar,
  aoErrar,
}: {
  servidor: ServidorOrigem;
  aoMudar: () => void;
  aoErrar: (e: unknown) => void;
}) {
  const [dominio, setDominio] = useState("");
  const [revenda, setRevenda] = useState("");
  const [caixa, setCaixa] = useState("");
  const [filtro, setFiltro] = useState("");

  // O INVENTÁRIO DO SERVIDOR, e não a conta do cPanel.
  //
  // A tela pedia a CONTA e só então descobria os domínios dela. Mas quem
  // migra pensa em domínio: `agenciacaosbr` é a conta de
  // `agenciacaos.com.br`, e `abraplaccom` é a de `abraplac.com.br` — ninguém
  // adivinha a segunda a partir da primeira. Pedir a conta era pedir
  // exatamente o dado que a pessoa não tem.
  //
  // Agora a direção se inverte: lista-se o servidor inteiro, ela escolhe o
  // DOMÍNIO, e a conta vem junto.
  const [buscaUUID, setBuscaUUID] = useState<string | null>(null);

  const asRevendas = useQuery({
    queryKey: ["p", "revendas", "para-migrar"],
    queryFn: () => revendas.listar({ por_pagina: 200 }),
  });

  const descobrir = useMutation({
    mutationFn: () =>
      migracao.enfileirar({ servidor_uuid: servidor.uuid!, estagio: "dominios" }),
    onSuccess: (t) => {
      setBuscaUUID(t.uuid ?? null);
      aoMudar();
    },
    onError: aoErrar,
  });

  const busca = useQuery({
    queryKey: ["p", "migracao", "inventario", buscaUUID],
    queryFn: () => migracao.trabalhos(),
    enabled: buscaUUID !== null,
    // Pergunta enquanto o trabalho corre e PARA quando termina. Uma tela que
    // pergunta para sempre é a que martela a API a noite toda no painel que
    // alguém deixou aberto.
    refetchInterval: (q) => {
      const t = q.state.data?.itens?.find((i) => i.uuid === buscaUUID);
      return t && t.estado !== "na fila" && t.estado !== "rodando" ? false : 2_000;
    },
  });

  const trabalhoDaBusca = busca.data?.itens?.find((t) => t.uuid === buscaUUID);
  const inventario = (trabalhoDaBusca?.relatorio as { inventario?: DominioDaOrigem[] } | null)
    ?.inventario;
  const buscando =
    descobrir.isPending ||
    (buscaUUID !== null &&
      (!trabalhoDaBusca ||
        trabalhoDaBusca.estado === "na fila" ||
        trabalhoDaBusca.estado === "rodando"));
  // O trabalhador roda fora da API e pode não estar no ar. Sem este aviso a
  // tela gira para sempre e quem olha conclui que o painel quebrou.
  const paradoNaFila =
    trabalhoDaBusca?.estado === "na fila" &&
    Date.now() - new Date(trabalhoDaBusca.criado_em ?? Date.now()).getTime() > 20_000;

  const escolhido = inventario?.find((d) => d.dominio === dominio);
  const conta = escolhido?.conta ?? "";

  const visiveis = (inventario ?? []).filter((d) => {
    const alvo = filtro.trim().toLowerCase();
    // Pela conta também: quem chega com "qual era mesmo a conta do fulano?"
    // digita o que lembra, e às vezes lembra da conta.
    return alvo === "" || d.dominio.includes(alvo) || d.conta.toLowerCase().includes(alvo);
  });

  // Bloqueia SÓ o que é perigoso. Domínio que já existe aqui e não veio
  // desta origem é outro cliente com o mesmo nome — migrar por cima
  // juntaria o e-mail de duas pessoas no mesmo endereço. Já ter vindo DESTA
  // origem é o caso da retomada, e esse continua liberado.
  const bloqueado = escolhido?.existe_aqui === true && escolhido.ja_migrado !== true;
  // Já ter mensagem aqui muda a operação de "trazer" para "sincronizar" —
  // mesma execução, expectativa muito diferente.
  const jaTemMensagens = (escolhido?.mensagens_aqui ?? 0) > 0;

  const enfileirar = useMutation({
    mutationFn: (
      estagio:
        | "conferir"
        | "completo"
        | "contas"
        | "mensagens"
        | "contatos"
        | "verificar"
        | "liberar"
        | "indexar",
    ) =>
      migracao.enfileirar({
        servidor_uuid: servidor.uuid!,
        conta,
        dominio: dominio || undefined,
        estagio,
        revenda_uuid: estagio === "contas" || estagio === "completo" ? revenda : undefined,
        caixa:
          estagio === "mensagens" || estagio === "completo"
            ? caixa.trim() || undefined
            : undefined,
      }),
    onSuccess: () => aoMudar(),
    onError: aoErrar,
  });

  const apagar = useMutation({
    mutationFn: () => migracao.removerServidor(servidor.uuid!),
    onSuccess: aoMudar,
    onError: aoErrar,
  });


  return (
    <>
      <Linha>
        <Celula>{servidor.nome}</Celula>
        <Celula>
          <span className="font-mono text-xs">
            {servidor.usuario}@{servidor.host}
            {servidor.porta !== 22 && `:${servidor.porta}`}
          </span>
        </Celula>
        <Celula>{servidor.migracoes ?? 0}</Celula>
        <Celula>
          <button onClick={() => apagar.mutate()} className="text-xs text-perigo underline">
            remover
          </button>
        </Celula>
      </Linha>
      <tr>
        <td colSpan={4} className="bg-fundo px-4 py-3">
          {/* DOIS MIGRADORES, DUAS TELAS. O fluxo do WHM gira em torno de
              ESCOLHER UM DOMÍNIO da lista que o servidor devolve; o do IMAP
              não tem lista nenhuma para oferecer — o protocolo não conta
              quais caixas existem, e quem sabe é quem tem a senha. Forçar os
              dois no mesmo formulário faria metade dos campos não se aplicar
              em cada caso.

              E SERVIDOR IMAP NÃO ABRE O FLUXO DO WHM: "Procurar domínios"
              tentaria um SSH que não existe, e falharia com um erro que não
              ensina nada. */}
          {servidor.tipo === "imap" ? (
            <p className="text-xs text-tinta-fraca">
              Esta origem é <strong>IMAP</strong>: ela não tem lista de domínios para
              oferecer, porque o protocolo não conta quais caixas existem. Para trazer
              uma caixa dela, use a aba <strong>Uma caixa (IMAP)</strong>, no topo
              desta tela.
            </p>
          ) : (
          <>
          {/* O FLUXO, na ordem em que se pensa:
              1. qual domínio do WHM
              2. de quem ele vai ser aqui
              3. migrar — ou o aviso de que já existe. */}

          {!inventario && !buscando && (
            <Botao icone="Search" aoClicar={() => descobrir.mutate()}>
              Ver os domínios deste servidor
            </Botao>
          )}
          {buscando && (
            <p className="text-sm text-acao">lendo o servidor…</p>
          )}
          {trabalhoDaBusca?.estado === "falhou" && (
            <p className="text-sm text-perigo">
              {trabalhoDaBusca.erro || "não consegui ler este servidor"}
            </p>
          )}
          {paradoNaFila && (
            <p className="mt-2 rounded-lg border border-aviso/40 bg-aviso/10 px-3 py-2 text-xs text-tinta">
              Parado na fila há mais de 20 segundos. Quem executa é um processo
              separado, que normalmente sobe junto com a plataforma — se ele
              caiu, nada sai da fila. Confira com{" "}
              <code className="font-mono">docker compose ps migrador</code> e
              suba com <code className="font-mono">docker compose up -d migrador</code>.
            </p>
          )}

          {inventario && (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
              {/* 1. O DOMÍNIO. Lista, não campo de texto: são centenas, e
                  digitar um nome de domínio de cabeça é errar uma letra e
                  receber "sem arquivo shadow" em vez de "não existe". */}
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="text-xs text-tinta-fraca">
                    1. Domínio no WHM — {inventario.length} encontrados
                  </span>
                  <span className="flex items-center gap-2">
                    <input
                      value={filtro}
                      onChange={(e) => setFiltro(e.target.value)}
                      placeholder="filtrar…"
                      className="w-40 rounded-lg border border-linha bg-white px-3 py-1.5 text-sm text-tinta"
                    />
                    {/* A LISTA É UM RETRATO, e envelhece assim que uma
                        migração termina: o domínio recém-trazido continuaria
                        aparecendo como nunca migrado, com o botão errado.
                        Reler é uma leitura, leva 2 segundos e não altera
                        nada — em nenhum dos dois lados. */}
                    <button
                      type="button"
                      onClick={() => descobrir.mutate()}
                      disabled={buscando}
                      title="Reler o servidor de origem"
                      className="rounded-full border border-linha px-3 py-1.5 text-xs text-tinta-media disabled:opacity-40"
                    >
                      {buscando ? "lendo…" : "reler"}
                    </button>
                  </span>
                </div>
                <div className="max-h-72 overflow-y-auto rounded-xl border border-linha bg-white">
                  {visiveis.length === 0 && (
                    <p className="px-3 py-4 text-sm text-tinta-fraca">
                      nenhum domínio com esse trecho.
                    </p>
                  )}
                  {visiveis.map((d) => (
                    <button
                      key={d.dominio}
                      type="button"
                      onClick={() => setDominio(d.dominio)}
                      className={`flex w-full items-center justify-between gap-3 border-b border-linha px-3 py-2 text-left last:border-b-0 ${
                        d.dominio === dominio ? "bg-acao/10" : "hover:bg-fundo"
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-tinta">{d.dominio}</span>
                        <span className="block truncate text-xs text-tinta-fraca">
                          conta {d.conta} · {d.caixas === 1 ? "1 caixa" : `${d.caixas} caixas`}
                          {d.mensagens_aqui > 0 &&
                            ` · ${d.mensagens_aqui.toLocaleString("pt-BR")} mensagens já aqui`}
                        </span>
                      </span>
                      {d.existe_aqui && (
                        <Selo
                          tom={d.ja_migrado ? "ok" : "erro"}
                          texto={d.ja_migrado ? "já migrado" : "já existe aqui"}
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* 2. DE QUEM É, E ENTÃO MIGRAR.
                  UM BOTÃO SÓ. Antes eram dois — "Migrar as caixas" e
                  "Trazer mensagens" — e a ordem entre eles não estava
                  escrita em lugar nenhum: quem apertasse o segundo primeiro
                  recebia "a caixa ainda não existe aqui". Pior, terminado o
                  primeiro nada dizia que faltava o segundo.

                  "Caixas" e "mensagens" era a divisão de quem ESCREVEU o
                  migrador, não a de quem migra. */}
              <div className="flex flex-col gap-3">
                <label className="text-xs text-tinta-fraca">
                  2. Revenda dona
                  <select
                    value={revenda}
                    onChange={(e) => setRevenda(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-linha bg-white px-3 py-2 text-sm text-tinta"
                  >
                    <option value="">escolha…</option>
                    {asRevendas.data?.itens?.map((r) => (
                      <option key={r.uuid} value={r.uuid ?? ""}>{r.nome}</option>
                    ))}
                  </select>
                  <span className="mt-1 block">
                    A única coisa que o cPanel não sabe: de quem é este cliente.
                  </span>
                </label>

                {/* O IMPEDIMENTO APARECE NA HORA DA ESCOLHA, não depois do
                    clique. Descobrir pelo erro é descobrir tarde. */}
                {bloqueado && (
                  <p className="rounded-lg border border-perigo/40 bg-perigo/10 px-3 py-2 text-xs text-tinta">
                    <strong>{dominio} já está cadastrado aqui</strong>, e não foi este
                    servidor que o trouxe. Migrar por cima juntaria o e-mail de duas
                    pessoas no mesmo endereço. Remova o domínio em{" "}
                    <strong>Domínios</strong> antes de migrar.
                  </p>
                )}

                <button
                  type="button"
                  disabled={!dominio || !revenda || bloqueado || enfileirar.isPending}
                  onClick={() => enfileirar.mutate("completo")}
                  className="flex items-center justify-center gap-2 rounded-full bg-acao px-5 py-3 text-base font-medium text-white disabled:opacity-40"
                >
                  <Icone nome={jaTemMensagens ? "RefreshCw" : "Download"} tamanho={18} />
                  {!dominio
                    ? "Escolha um domínio"
                    : jaTemMensagens
                      ? "Sincronizar este domínio"
                      : `Migrar ${escolhido?.caixas ?? 0} ${escolhido?.caixas === 1 ? "caixa" : "caixas"}`}
                </button>

                {/* CONFERIR é o que autoriza virar o MX, e por isso fica
                    ao lado do botão de migrar — não escondido num canto.
                    Só lê dos dois lados, pode rodar a qualquer momento. */}
                {dominio && escolhido?.existe_aqui && (
                  <button
                    type="button"
                    disabled={enfileirar.isPending}
                    onClick={() => enfileirar.mutate("verificar")}
                    className="flex items-center justify-center gap-2 rounded-full border border-linha px-4 py-2 text-sm text-tinta-media disabled:opacity-40"
                  >
                    <Icone nome="Search" tamanho={16} />
                    Conferir contra a origem
                  </button>
                )}

                {/* A AGENDA, separada do botão principal — e o motivo é
                    histórico: quatro domínios foram migrados antes de ela
                    existir. Sem este botão, a única forma de buscar a agenda
                    deles seria re-sincronizar as mensagens, o que custa horas
                    de cópia para trazer alguns kilobytes de contato.
                    A partir de agora ela vem junto no botão de cima. */}
                {dominio && escolhido?.existe_aqui && (
                  <button
                    type="button"
                    disabled={enfileirar.isPending}
                    onClick={() => enfileirar.mutate("contatos")}
                    className="flex items-center justify-center gap-2 rounded-full border border-linha px-4 py-2 text-sm text-tinta-media disabled:opacity-40"
                  >
                    <Icone nome="Users" tamanho={16} />
                    Trazer a agenda de contatos
                  </button>
                )}

                {/* A ÁREA DE POUSO com o NÚMERO na frente. "Libere espaço"
                    sem dizer quanto é um pedido que ninguém sabe avaliar. */}
                {dominio && (escolhido?.pouso_bytes ?? 0) > 0 && (
                  <div className="rounded-xl border border-linha bg-white p-3 text-xs text-tinta-fraca">
                    <p className="text-tinta">
                      <strong>{tamanho(escolhido!.pouso_bytes!)}</strong> na área de
                      pouso — a cópia de trabalho deste domínio.
                    </p>
                    <p className="mt-1">
                      Ela deixa a próxima sincronização rápida: sem ela, o rsync baixa
                      tudo de novo para trazer as mensagens que chegaram depois.
                      Liberar só vale quando o disco aperta, ou quando a migração
                      terminou de vez.
                    </p>
                    <button
                      type="button"
                      disabled={enfileirar.isPending}
                      onClick={() => enfileirar.mutate("liberar")}
                      className="mt-2 text-perigo underline disabled:opacity-40"
                    >
                      liberar {tamanho(escolhido!.pouso_bytes!)}
                    </button>
                    {/* O CONSERTO DO MOTIVO MAIS COMUM DE A LIBERAÇÃO SER
                        RECUSADA. As três provas incluem "o índice do webmail
                        está em dia", e ele fica para trás nas caixas que
                        ninguém abriu — por desenho, porque o laço só visita
                        caixa com sessão recente.

                        Quando isso acontece, a recusa diz "as mensagens
                        chegaram mas o cliente ainda não as vê" — e sem este
                        botão o operador lia a frase sem ter como agir. Ele só
                        lê do Dovecot e escreve no índice: não toca na origem
                        nem na caixa. */}
                    <button
                      type="button"
                      disabled={enfileirar.isPending}
                      onClick={() => enfileirar.mutate("indexar")}
                      className="mt-2 ml-4 text-acao underline disabled:opacity-40"
                    >
                      pôr o índice do webmail em dia
                    </button>
                  </div>
                )}

                {/* O QUE VAI ACONTECER, em ordem, antes de acontecer. */}
                {dominio && !bloqueado && (
                  <div className="rounded-xl border border-linha bg-white p-3 text-xs text-tinta-fraca">
                    {jaTemMensagens ? (
                      <>
                        <p className="text-tinta">
                          <strong>
                            {escolhido!.mensagens_aqui.toLocaleString("pt-BR")} mensagens
                          </strong>{" "}
                          já estão aqui
                          {escolhido!.ultima_sincronizacao &&
                            `, desde ${dataCurta(escolhido!.ultima_sincronizacao)}`}
                          .
                        </p>
                        <p className="mt-1">
                          Sincronizar traz <strong>só o que chegou depois</strong>. Cada
                          mensagem já trazida está registrada, então nada vem duas vezes
                          — pode rodar quantas vezes quiser, inclusive no dia da virada
                          de MX.
                        </p>
                        <p className="mt-1">
                          A agenda de contatos vem junto, e também não duplica: o que
                          já está aqui é mantido como está, inclusive um nome que você
                          tenha corrigido à mão.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-tinta">Em um passo só, nesta ordem:</p>
                        <ol className="mt-1 list-decimal pl-4">
                          <li>cria o domínio e as {escolhido?.caixas ?? 0} caixas, com
                            as senhas que o cliente já usa</li>
                          <li>traz os encaminhamentos e as respostas automáticas</li>
                          <li>traz a agenda de contatos de cada caixa</li>
                          <li>copia as mensagens, com as datas e as marcas de lida e
                            respondida originais</li>
                        </ol>
                        <p className="mt-2">
                          <strong>Nada é alterado na origem.</strong> Um domínio grande
                          leva horas, às vezes dias — pode ser interrompido, e rodar de
                          novo continua de onde parou sem duplicar.
                        </p>
                      </>
                    )}

                    {/* PROVAR COM UMA CAIXA continua possível, mas deixou de
                        ser um passo do fluxo: é a exceção de quem está
                        conferindo um servidor novo, não o caminho normal. */}
                    <details className="mt-2">
                      <summary className="cursor-pointer text-acao">
                        só uma caixa, para conferir antes
                      </summary>
                      <div className="mt-2">
                        <input
                          value={caixa}
                          onChange={(e) => setCaixa(e.target.value)}
                          placeholder="ex.: contato"
                          className="w-full rounded-lg border border-linha bg-white px-3 py-2 text-sm text-tinta"
                        />
                        <p className="mt-1">
                          Em branco = o domínio inteiro. Útil num servidor de origem
                          novo: conferir contagem e datas numa caixa custa minutos,
                          descobrir um erro depois de horas de cópia custa a migração.
                        </p>
                      </div>
                    </details>
                  </div>
                )}
              </div>
            </div>
          )}
          </>
          )}
        </td>
      </tr>
    </>
  );
}

// MIGRAR UMA CAIXA, POR IMAP — a tela inteira num passo só.
//
// O FORMATO É DUAS COLUNAS, origem à esquerda e destino à direita, porque é
// assim que a operação É: mover de um lado para o outro. O Jhou mostrou uma
// ferramenta de mercado com esse desenho e ele está certo — a tela anterior
// escondia isto dentro da linha de um servidor que precisava ser cadastrado
// antes, e cadastrar servidor é conceito do caminho WHM, onde se migra
// dezenas de domínios da mesma máquina. Numa caixa avulsa, é um passo que
// existe só porque o nosso modelo de dados precisa dele — e isso é problema
// nosso, não de quem opera. Agora o servidor nasce da própria migração.
//
// E AS DUAS COLUNAS NÃO SÃO SIMÉTRICAS, de propósito. A ferramenta de
// referência pede endereço e senha dos dois lados; aqui o destino é SEMPRE
// este servidor, e o trabalhador já entra nele com a chave-mestra do Dovecot.
// Pedir o que já sabemos seria pedir ao operador uma informação que ele não
// tem motivo para digitar — e abrir uma segunda chance de errar.
function MigrarUmaCaixa({
  soIMAP = false,
  aoMudar,
  aoErrar,
}: {
  /** Revenda: porta fixa em 993 e sem falar do migrador do WHM. */
  soIMAP?: boolean;
  aoMudar: () => void;
  aoErrar: (e: unknown) => void;
}) {
  const [host, setHost] = useState("");
  const [porta, setPorta] = useState("993");
  const [usuario, setUsuario] = useState("");
  const [senha, setSenha] = useState("");
  const [caixa, setCaixa] = useState("");
  const [enviado, setEnviado] = useState<"testar" | "imap" | null>(null);

  // A LISTA DE CAIXAS, em vez de um campo de texto. O destino TEM que existir
  // aqui — escolher de uma lista torna "não existe" impossível, em vez de um
  // erro que só aparece depois de enfileirar.
  const minhasCaixas = useQuery({
    queryKey: ["p", "migracao", "caixas-destino"],
    queryFn: () => caixas.listar({ por_pagina: 200 }),
  });

  const enfileirar = useMutation({
    mutationFn: (estagio: "testar" | "imap") =>
      migracao.enfileirar({
        estagio,
        origem_host: host.trim(),
        origem_porta: Number.parseInt(porta, 10) || 993,
        origem_usuario: usuario.trim(),
        origem_senha: senha,
        caixa: caixa.trim(),
        dominio: caixa.split("@")[1] ?? "",
      }),
    onSuccess: (_, estagio) => {
      // A SENHA SAI DA MEMÓRIA DA TELA assim que o trabalho é aceito. Ela já
      // foi selada no servidor; deixá-la num campo aberto só aumenta o tempo
      // em que existe em claro em algum lugar.
      setSenha("");
      setEnviado(estagio);
      aoMudar();
    },
    onError: aoErrar,
  });

  const completo = host.trim() !== "" && usuario.trim() !== "" && senha !== "" && caixa.includes("@");

  return (
    <div className="mb-6">
      <div className="grid gap-px overflow-hidden rounded-2xl border border-linha bg-linha md:grid-cols-[1fr_auto_1fr]">
        {/* ORIGEM */}
        <div className="bg-white p-5">
          <p className="mb-4 flex items-center gap-2 text-base font-medium text-tinta">
            <Icone nome="Forward" tamanho={18} className="text-acao" />
            Origem
          </p>
          <div className="grid gap-3 sm:grid-cols-[1fr_6rem]">
            <Campo rotulo="Servidor IMAP" valor={host} aoMudar={setHost} dica="ex.: mail.cliente.com.br" />
            {soIMAP ? (
              // A revenda migra pela 993, sempre: TLS com o certificado
              // conferido. A API recusa outra porta; mostrar um campo que só
              // aceita um valor seria convite ao erro.
              <div className="text-xs text-tinta-fraca">
                Porta
                <span className="mt-1 block rounded-lg border border-linha bg-fundo px-3 py-2 text-sm text-tinta">
                  993
                </span>
                <span className="mt-1 block">IMAP com TLS</span>
              </div>
            ) : (
              <Campo rotulo="Porta" valor={porta} aoMudar={setPorta} tipo="number" dica="993 = TLS" />
            )}
            <Campo rotulo="E-mail" valor={usuario} aoMudar={setUsuario}
              dica="Como a pessoa entra hoje." classe="sm:col-span-2" />
            {/* `type="password"`, ao contrário do campo de senha NOVA na tela
                de Caixas: aquela vai ser ditada ao cliente agora e precisa ser
                lida; esta é a senha ATUAL de outra pessoa. */}
            <Campo rotulo="Senha" valor={senha} aoMudar={setSenha} tipo="password"
              dica="Selada no servidor antes de ir para o banco." classe="sm:col-span-2" />
          </div>
        </div>

        {/* A SETA diz a direção, que é a única coisa que não se desfaz. */}
        <div className="flex items-center justify-center bg-white px-3 py-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full border border-acao/40 bg-acao/10">
            <Icone nome="ArrowRightLeft" tamanho={16} className="text-acao" />
          </div>
        </div>

        {/* DESTINO — sem servidor e sem senha, e a tela diz por quê. */}
        <div className="bg-white p-5">
          <p className="mb-4 flex items-center gap-2 text-base font-medium text-tinta">
            <Icone nome="Inbox" tamanho={18} className="text-acao" />
            Destino
          </p>
          <div className="grid gap-3">
            <div className="rounded-lg border border-linha bg-fundo px-3 py-2 text-xs text-tinta-fraca">
              <span className="block text-tinta">Este servidor</span>
              Não pedimos endereço nem senha do destino: ele é nosso, e a entrega
              usa a chave do próprio servidor. Uma senha a menos para digitar errado.
            </div>
            <label className="text-xs text-tinta-fraca">
              Caixa que vai receber
              <select
                value={caixa}
                onChange={(e) => setCaixa(e.target.value)}
                className="mt-1 w-full rounded-lg border border-linha bg-white px-3 py-2 text-sm text-tinta"
              >
                <option value="">Escolha uma caixa…</option>
                {(minhasCaixas.data?.itens ?? []).map((c) => (
                  <option key={c.uuid} value={c.email}>{c.email}</option>
                ))}
              </select>
              <span className="mt-1 block">
                Ela precisa existir antes. Se não estiver na lista, crie em Caixas.
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* O QUE ESTE CAMINHO NÃO TRAZ, ANTES DO CLIQUE. Descobrir depois que a
          senha do cliente não veio junto é descobrir tarde. */}
      <div className="mt-3 rounded-xl border border-aviso/40 bg-aviso/10 p-3 text-xs text-tinta">
        <strong>Por IMAP vêm só as mensagens.</strong>{" "}
        <span className="text-tinta-media">
          Senha, cota, encaminhador, resposta automática e agenda ficam de fora — o
          protocolo não os expõe, e o cliente passa a usar a senha que você definiu
          aqui.
          {!soIMAP &&
            " Quando a origem for um WHM/cPanel com SSH, o outro migrador traz " +
              "tudo e ninguém troca de senha."}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {/* TESTAR PRIMEIRO, e por isso ele vem antes. Login e lista de pastas
            levam segundos; descobrir a senha errada depois de uma hora de
            cópia é o pior jeito de descobrir. */}
        <Botao tom="neutro" icone="Search" desabilitado={!completo}
          ocupado={enfileirar.isPending} aoClicar={() => enfileirar.mutate("testar")}>
          Testar conexão
        </Botao>
        <Botao icone="Download" desabilitado={!completo}
          ocupado={enfileirar.isPending} aoClicar={() => enfileirar.mutate("imap")}>
          Migrar as mensagens
        </Botao>
        {enviado && (
          <span className="text-xs text-tinta-fraca">
            {enviado === "testar" ? "Teste" : "Migração"} na fila — o resultado aparece
            no histórico abaixo.
          </span>
        )}
      </div>

      <p className="mt-2 text-xs text-tinta-fraca">
        Rodar de novo <strong>não duplica</strong>: o que já chegou é reconhecido e só
        vem o que entrou depois — pode rodar no dia da virada de MX. As datas originais
        são preservadas, e a pasta de spam da origem cai na Lixeira eletrônica daqui em
        vez de virar uma pasta solta.
      </p>
    </div>
  );
}

// ANDAMENTO: o que está acontecendo AGORA.
//
// Sem isto o painel mostrava "rodando" por horas, e "indo bem", "muito
// lento" e "travado" produziam exatamente a mesma tela — quem olhava não
// tinha como decidir entre esperar e ir atrás do problema.
//
// A barra é a das CAIXAS, não a das mensagens: é ela que mede o trabalho
// inteiro. A pasta e a contagem de mensagens vão no texto, porque é o que se
// move visivelmente e prova que não travou.
function Andamento({ p }: { p: ProgressoMigracao }) {
  // UMA fração, que não recua e não mente sobre o quanto falta.
  //
  // Duas versões anteriores erraram, cada uma de um jeito. Medir só a pasta
  // atual fazia a barra DESPENCAR ao sair da INBOX de 4.401 mensagens para a
  // Lixeira de 136. Medir pastas como unidades iguais parou a barra em 14%
  // numa caixa de 7 pastas em que só a INBOX tinha mensagem — e então pulou
  // para o fim.
  //
  // A unidade honesta é MENSAGEM: a caixa preenche uma fatia do trabalho, na
  // proporção das mensagens que ela tem.
  const naCaixa = p.caixa_total > 0 ? p.caixa_feitas / p.caixa_total : 0;
  const fracao =
    p.caixas_total > 0
      ? Math.min(1, (p.caixas_feitas + naCaixa) / p.caixas_total)
      : 0;

  return (
    <div className="mt-1 w-56 max-w-full">
      {p.caixas_total > 0 && (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-linha">
          <div
            className="h-full bg-acao transition-[width] duration-500"
            style={{ width: `${Math.round(fracao * 100)}%` }}
          />
        </div>
      )}
      <p className="mt-1 truncate text-xs text-tinta-fraca" title={p.caixa ?? ""}>
        {p.fase}
        {p.caixas_total > 1 && ` · caixa ${p.caixas_feitas + 1}/${p.caixas_total}`}
        {p.caixas_total > 0 && ` · ${Math.round(fracao * 100)}%`}
      </p>
      {p.caixa && (
        <p className="truncate text-xs text-tinta" title={p.caixa}>
          {p.caixa}
          {p.pasta && <span className="text-tinta-fraca"> · {p.pasta}</span>}
        </p>
      )}
      {p.caixa_total > 0 && (
        <p className="text-xs text-tinta-fraca tabular-nums">
          {p.caixa_feitas}/{p.caixa_total} mensagens · {p.importadas} trazidas
          {p.puladas > 0 && ` · ${p.puladas} já estavam`}
          {p.bytes > 0 && ` · ${tamanho(p.bytes)}`}
        </p>
      )}
    </div>
  );
}

function tamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const unidades = ["kB", "MB", "GB", "TB"];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < unidades.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${unidades[i]}`;
}

const TOM_DO_ESTADO: Record<string, "ok" | "aviso" | "erro" | "neutro"> = {
  "na fila": "neutro",
  rodando: "aviso",
  concluido: "ok",
  // Terminou, mas faltou coisa. Nunca verde: verde aqui é o que faria
  // alguém virar o MX em cima de uma caixa incompleta.
  parcial: "aviso",
  falhou: "erro",
  cancelado: "neutro",
};

function LinhaDoTrabalho({
  trabalho,
  aoMudar,
  semConta = false,
}: {
  trabalho: TrabalhoMigracao;
  aoMudar: () => void;
  /** No caminho IMAP não existe conta de painel: a coluna sai inteira. */
  semConta?: boolean;
}) {
  const [aberto, setAberto] = useState(false);
  const apagar = useMutation({
    mutationFn: () => migracao.removerTrabalho(trabalho.uuid!),
    onSuccess: aoMudar,
  });
  const temResultado = !!trabalho.relatorio || !!trabalho.erro;
  const progresso = trabalho.progresso as ProgressoMigracao | null | undefined;

  return (
    <>
      <Linha>
        <Celula>{dataCurta(trabalho.criado_em)}</Celula>
        <Celula>{trabalho.servidor}</Celula>
        {!semConta && <Celula>{trabalho.conta}</Celula>}
        <Celula>{trabalho.dominio || "—"}</Celula>
        <Celula>
          <Selo texto={trabalho.estado ?? "?"} tom={TOM_DO_ESTADO[trabalho.estado ?? ""] ?? "neutro"} />
          {progresso && <Andamento p={progresso} />}
        </Celula>
        <Celula>
          <span className="flex gap-3 whitespace-nowrap">
            {temResultado && (
              <button onClick={() => setAberto((a) => !a)} className="text-xs text-acao underline">
                {aberto ? "fechar" : "ver"}
              </button>
            )}
            {trabalho.estado !== "rodando" && (
              <button onClick={() => apagar.mutate()} className="text-xs text-perigo underline">
                remover
              </button>
            )}
          </span>
        </Celula>
      </Linha>
      {aberto && (
        <tr>
          <td colSpan={semConta ? 5 : 6} className="bg-fundo px-4 py-4">
            {trabalho.erro && <p className="mb-3 text-sm text-perigo">{trabalho.erro}</p>}
            {trabalho.relatorio != null && <Relatorio dados={trabalho.relatorio as Record<string, unknown>} />}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * O relatório de uma conferência.
 *
 * O que interessa a quem decide migrar é a CONSEQUÊNCIA — quantas caixas
 * entram com a mesma senha, quantas vão precisar trocar. O nome do algoritmo
 * de hash não diz isso, e é o detalhe, não a resposta.
 */
// A AGENDA no relatório.
//
// Vive em componente próprio porque aparece em DOIS lugares: no relatório do
// estágio `contatos`, sozinho, e dentro do relatório do `completo`, ao lado
// das caixas e das mensagens. Duas cópias divergiriam no dia em que um campo
// novo entrasse — como já aconteceu com os cabeçalhos de conversa.
function AgendaDoRelatorio({ dados }: { dados: Record<string, unknown> }) {
  const num = (k: string) => Number(dados[k] ?? 0);
  const porFonte = (dados.por_fonte ?? {}) as Record<string, number>;
  const avisos = (dados.avisos as string[]) ?? [];
  return (
    <div>
      <p className="font-medium">Agenda de contatos</p>
      <ul className="mt-1 space-y-0.5 text-xs">
        <li className="text-acao">
          <strong>{num("gravados").toLocaleString("pt-BR")}</strong> contatos novos
        </li>
        <li className="text-tinta-fraca">
          {num("ja_estavam").toLocaleString("pt-BR")} já estavam aqui
        </li>
        {/* O CARTÃO SEM ENDEREÇO É CONTADO, não escondido. São 130 dos 4.594
            do servidor de origem, e a nossa agenda exige endereço — 130
            contatos desaparecendo em silêncio é o que isto evita. */}
        {num("sem_endereco") > 0 && (
          <li className="text-tinta-fraca">
            {num("sem_endereco")} cartões na origem tinham só o nome, sem endereço —
            a agenda daqui precisa do endereço, então ficaram de fora
          </li>
        )}
        {Object.keys(porFonte).length > 0 && (
          <li className="text-tinta-fraca">
            de onde veio:{" "}
            {Object.entries(porFonte)
              .map(([fonte, n]) => `${fonte} (${n})`)
              .join(" · ")}
          </li>
        )}
      </ul>
      {avisos.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-tinta-fraca">
          {avisos.slice(0, 4).map((a, i) => <li key={i}>{a}</li>)}
          {avisos.length > 4 && <li>… e mais {avisos.length - 4}</li>}
        </ul>
      )}
    </div>
  );
}

function Relatorio({ dados }: { dados: Record<string, unknown> }) {
  const n = (chave: string) => Number(dados[chave] ?? 0);
  const dominios = dados.dominios as string[] | undefined;

  // A CONFERÊNCIA: a tabela que autoriza (ou não) virar o MX.
  if (Array.isArray(dados.caixas)) {
    const caixas = dados.caixas as {
      caixa: string; origem: number; aqui: number; livro: number; diferenca: number;
    }[];
    const faltando = caixas.filter((c) => c.diferenca !== 0);
    return (
      <div className="text-sm text-tinta">
        <p className={faltando.length === 0 ? "font-medium text-acao" : "font-medium text-aviso"}>
          {faltando.length === 0
            ? `Tudo conferido: ${n("iguais")} caixas iguais, ${n("total_origem").toLocaleString("pt-BR")} mensagens dos dois lados.`
            : `${n("diferentes")} de ${caixas.length} caixas com diferença.`}
        </p>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs tabular-nums">
            <thead className="text-tinta-fraca">
              <tr>
                <th className="py-1 pr-3 text-left font-normal">caixa</th>
                <th className="py-1 pr-3 text-right font-normal">origem</th>
                <th className="py-1 pr-3 text-right font-normal">aqui</th>
                <th className="py-1 pr-3 text-right font-normal">livro</th>
                <th className="py-1 text-right font-normal">falta</th>
              </tr>
            </thead>
            <tbody>
              {caixas.map((c) => (
                <tr key={c.caixa} className={c.diferenca !== 0 ? "text-aviso" : ""}>
                  <td className="py-0.5 pr-3">{c.caixa}</td>
                  <td className="py-0.5 pr-3 text-right">{c.origem.toLocaleString("pt-BR")}</td>
                  <td className="py-0.5 pr-3 text-right">{c.aqui.toLocaleString("pt-BR")}</td>
                  <td className="py-0.5 pr-3 text-right">{c.livro.toLocaleString("pt-BR")}</td>
                  <td className="py-0.5 text-right">
                    {c.diferenca === 0 ? "—" : c.diferenca}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {faltando.length > 0 && (
          <p className="mt-2 text-xs text-tinta-fraca">
            Sincronize o domínio e confira de novo. A coluna <strong>livro</strong> é o
            que registramos ter trazido: se ela bate com <strong>aqui</strong> e as duas
            ficam abaixo da <strong>origem</strong>, são mensagens que chegaram depois —
            o caso normal numa caixa viva.
          </p>
        )}
      </div>
    );
  }

  // Liberação da área de pouso.
  if (dados.bytes_liberados !== undefined) {
    return (
      <div className="text-sm text-tinta">
        <p className="font-medium">
          {tamanho(Number(dados.bytes_liberados))} liberados de{" "}
          {String(dados.dominio ?? "")}
        </p>
        {dados.aviso ? (
          <p className="mt-1 text-xs text-tinta-fraca">{String(dados.aviso)}</p>
        ) : null}
      </div>
    );
  }

  // O estágio `contatos`, sozinho. Vem ANTES do relatório de conferência
  // porque os dois têm o campo `caixas`: sem esta ordem, a agenda cairia no
  // relatório de conferência e apareceria como "N caixas · formato ?".
  if (dados.lidos_na_origem !== undefined) {
    const falhas = (dados.falhas as string[]) ?? [];
    return (
      <div className="text-sm text-tinta">
        <AgendaDoRelatorio dados={dados} />
        <p className="mt-2 text-xs text-tinta-fraca">
          Lidos {Number(dados.lidos_na_origem ?? 0).toLocaleString("pt-BR")} na
          origem, em {Number(dados.caixas ?? 0)} caixa(s). Nada foi alterado lá, e
          nada que já estava aqui foi sobrescrito — rodar de novo só acrescenta.
        </p>
        {falhas.length > 0 && (
          <div className="mt-3 rounded-lg border border-aviso/40 bg-aviso/10 p-2">
            <p className="text-xs font-medium">{falhas.length} problema(s):</p>
            <ul className="mt-1 space-y-1 text-xs text-tinta-media">
              {falhas.slice(0, 8).map((f, i) => <li key={i}>{f}</li>)}
              {falhas.length > 8 && <li>… e mais {falhas.length - 8}</li>}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // O estágio `completo` devolve os dois passos aninhados. Mostrar o JSON
  // cru seria devolver a quem usa o formato interno do migrador.
  if (dados.contas || dados.mensagens) {
    const c = (dados.contas ?? {}) as Record<string, unknown>;
    const m = (dados.mensagens ?? {}) as Record<string, unknown>;
    const num = (o: Record<string, unknown>, k: string) => Number(o[k] ?? 0);
    const agenda = dados.contatos as Record<string, unknown> | undefined;
    const falhas = [
      ...((c.problemas as string[]) ?? []),
      ...((m.falhas as string[]) ?? []),
      ...((agenda?.falhas as string[]) ?? []),
    ];
    // SEPARADO DAS FALHAS, e com outra cor: aviso é comportamento correto
    // dito em voz alta — caixa desativada de propósito cujo histórico já
    // veio, por exemplo. Jogar os dois no mesmo balde vermelho faz quem
    // opera parar de ler os dois.
    const avisos = (m.avisos as string[]) ?? [];
    return (
      <div className="text-sm text-tinta">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="font-medium">Caixas</p>
            <ul className="mt-1 space-y-0.5 text-xs">
              <li>{num(c, "caixas_criadas")} criadas · {num(c, "caixas_puladas")} já existiam</li>
              <li>{num(c, "aliases_criados")} encaminhamentos</li>
              {num(c, "desativadas") > 0 && (
                <li className="text-tinta-fraca">
                  {num(c, "desativadas")} desativadas (travadas na origem)
                </li>
              )}
            </ul>
          </div>
          <div>
            <p className="font-medium">Mensagens</p>
            <ul className="mt-1 space-y-0.5 text-xs">
              <li className="text-acao">
                <strong>{num(m, "mensagens_importadas").toLocaleString("pt-BR")}</strong> trazidas agora
              </li>
              <li className="text-tinta-fraca">
                {num(m, "mensagens_ja_estavam").toLocaleString("pt-BR")} já estavam aqui
              </li>
              {Array.isArray(m.pastas) && (m.pastas as string[]).length > 0 && (
                <li className="text-tinta-fraca">
                  pastas: {(m.pastas as string[]).join(", ")}
                </li>
              )}
            </ul>
          </div>
          {agenda && <AgendaDoRelatorio dados={agenda} />}
        </div>
        {falhas.length > 0 && (
          <div className="mt-3 rounded-lg border border-aviso/40 bg-aviso/10 p-2">
            <p className="text-xs font-medium">
              {falhas.length} problema(s) — a migração NÃO está completa:
            </p>
            <ul className="mt-1 space-y-1 text-xs text-tinta-media">
              {falhas.slice(0, 8).map((f, i) => <li key={i}>{f}</li>)}
              {falhas.length > 8 && <li>… e mais {falhas.length - 8}</li>}
            </ul>
          </div>
        )}
        {avisos.length > 0 && (
          <div className="mt-3 rounded-lg border border-linha bg-linha-suave p-2">
            <p className="text-xs font-medium text-tinta-media">
              {avisos.length} observação(ões) — nada a consertar:
            </p>
            <ul className="mt-1 space-y-1 text-xs text-tinta-fraca">
              {avisos.slice(0, 8).map((a, i) => <li key={i}>{a}</li>)}
              {avisos.length > 8 && <li>… e mais {avisos.length - 8}</li>}
            </ul>
          </div>
        )}
      </div>
    );
  }

  if (dominios) {
    return (
      <div className="text-sm text-tinta">
        <p className="mb-2 font-medium">Domínios com e-mail nesta conta:</p>
        <ul className="grid gap-1 sm:grid-cols-3">
          {dominios.map((d) => (
            <li key={d} className="font-mono text-xs">{d}</li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-tinta-fraca">
          Confira um deles pondo o nome no campo "Domínio", acima.
        </p>
      </div>
    );
  }

  const porHash = (dados.por_formato_de_hash ?? {}) as Record<string, number>;
  return (
    <div className="grid gap-4 text-sm sm:grid-cols-2">
      <div>
        <p className="font-medium text-tinta">
          {n("caixas")} caixas · formato {String(dados.formato ?? "?")}
        </p>
        <ul className="mt-2 space-y-1">
          <li className="text-acao">
            <strong>{n("senha_preservada")}</strong> entram com a MESMA senha
          </li>
          {n("senha_nova") > 0 && (
            <li className="text-aviso">
              <strong>{n("senha_nova")}</strong> precisam de senha nova
            </li>
          )}
          {n("travadas") > 0 && (
            <li className="text-tinta-fraca">
              <strong>{n("travadas")}</strong> travadas na origem — entram desativadas
            </li>
          )}
        </ul>
        <ul className="mt-3 space-y-0.5 text-xs text-tinta-fraca">
          {Object.entries(porHash).map(([k, v]) => (
            <li key={k}>{k}: {v}</li>
          ))}
        </ul>
      </div>

      <div className="space-y-1 text-xs text-tinta-media">
        <p>
          <strong className="text-tinta">Cotas:</strong>{" "}
          {n("com_cota") > 0
            ? `${n("com_cota")} caixas com cota própria`
            : "nenhuma cota por caixa foi lida"}
        </p>
        <p>
          <strong className="text-tinta">Encaminhamentos:</strong> {n("aliases")}
          {dados.catch_all ? ` · catch-all → ${String(dados.catch_all)}` : ""}
        </p>
        {!!dados.tem_filtros && <p>Tem filtros na origem (Exim; os nossos são Sieve).</p>}
        {Array.isArray(dados.avisos) &&
          (dados.avisos as string[]).map((a) => (
            <p key={a} className="text-aviso">aviso: {a}</p>
          ))}
        {/* O conteúdo cru aparece de propósito: o formato do arquivo de cota
            varia entre versões do cPanel, e ver o que há de verdade é o que
            evita supor errado. */}
        {Array.isArray(dados.amostra_quota) && (dados.amostra_quota as string[]).length > 0 && (
          <details className="mt-2">
            <summary className="cursor-pointer">amostra do arquivo de cota</summary>
            <pre className="mt-1 overflow-x-auto rounded bg-white p-2 text-[11px]">
              {(dados.amostra_quota as string[]).join("\n")}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

function Campo({
  rotulo,
  valor,
  aoMudar,
  tipo = "text",
  dica,
  classe = "",
  aoSair,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  tipo?: "text" | "number" | "password";
  dica?: string;
  classe?: string;
  aoSair?: () => void;
}) {
  return (
    <label className={`text-xs text-tinta-fraca ${classe}`}>
      {rotulo}
      <input
        type={tipo}
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        onBlur={aoSair}
        className="mt-1 w-full rounded-lg border border-linha bg-white px-3 py-2 text-sm text-tinta"
      />
      {dica && <span className="mt-1 block">{dica}</span>}
    </label>
  );
}
